// Dấu "!" nhỏ thu gọn lời hướng dẫn phụ (CEO chốt 1a · 2a · 3a · 4a · 5b,
// 28/10/2026): chữ xám kiểu "Enter gửi · Shift+Enter xuống dòng…" hay câu giải
// thích cơ chế không còn nằm lù lù trên màn — rê chuột (máy tính) hoặc chạm
// (điện thoại) vào dấu "!" mới hiện.
//
// ĐỪNG dùng cho: câu trạng thái (trống, đang tải, lỗi, bị khoá), câu cảnh báo
// hậu quả (không đổi được sau khi tạo, có phí…), số liệu của từng đơn, và màn
// khách xem — những thứ đó người dùng PHẢI thấy ngay.
//
// Vòng "!" viền mảnh, không tô đặc (CEO chốt 03/09/2026 ở ExplainerButton):
// tô đậm thì người ta đọc ra là cảnh báo hay nút bị khoá.
//
// Lời giải thích luôn nằm trong DOM, chỉ ẩn bằng thuộc tính `hidden`: máy đọc
// màn hình vẫn đọc được qua aria-describedby, và chữ không biến khỏi trang.
import { useEffect, useId, useRef, useState, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Props = {
  children: ReactNode;
  /** Nhãn đọc cho máy đọc màn hình. */
  label?: string | undefined;
  /** Bong bóng mọc từ mép nào của dấu "!". Dấu nằm sát mép trái thì dùng "start". */
  align?: "start" | "end";
  className?: string;
};

export function HintTip({ children, label = "Xem giải thích", align = "end", className }: Props) {
  // "hover" = mở do rê chuột, rời chuột là đóng; "click" = bấm/chạm, đứng yên tới
  // khi chạm ra ngoài. Bấm vào dấu đang mở do rê thì GHIM lại chứ không đóng —
  // trên máy tính cú bấm luôn đến sau cú rê.
  const [open, setOpen] = useState<false | "hover" | "click">(false);
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <span
      ref={root}
      className={cn("relative inline-flex align-middle", className)}
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") setOpen((v) => v || "hover");
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "mouse") setOpen((v) => (v === "hover" ? false : v));
      }}
    >
      <button
        type="button"
        className="hint-tip"
        aria-label={label}
        aria-expanded={open !== false}
        aria-describedby={id}
        onClick={(e) => {
          // Dấu "!" hay nằm trong hàng/thẻ bấm được — đừng để cú chạm mở luôn dòng đó.
          e.stopPropagation();
          setOpen((v) => (v === "click" ? false : "click"));
        }}
      >
        !
      </button>
      <span
        id={id}
        role="tooltip"
        hidden={open === false}
        className={cn(
          "absolute top-full z-50 mt-1.5 block w-max max-w-[min(20rem,calc(100vw-2rem))] whitespace-normal rounded-md border bg-white p-2.5 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-[var(--color-text)] shadow-md",
          align === "end" ? "right-0" : "left-0",
        )}
      >
        {children}
      </span>
    </span>
  );
}

type NoteProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  children: ReactNode;
  label?: string | undefined;
};

/**
 * Bản đứng thành dòng của HintTip — thay chỗ cho một đoạn chữ phụ cũ: dòng chữ
 * dài co lại còn dấu "!" ở góc phải.
 */
export function HintNote({ children, label, className, ...rest }: NoteProps) {
  return (
    <div className={className} {...rest}>
      <div className="flex justify-end">
        <HintTip label={label}>{children}</HintTip>
      </div>
    </div>
  );
}
